import type { DocProps } from '@affine/core/blocksuite/initialization';
import type { DocsService } from '@affine/core/modules/doc';
import type { WorkspaceService } from '@affine/core/modules/workspace';
import {
  DEFAULT_PAGE_BLOCK_HEIGHT,
  DEFAULT_PAGE_BLOCK_WIDTH,
} from '@blocksuite/affine/model';

export const LIBRARY_KIND = 'library-item';

export interface CreateLibraryItemOptions {
  docsService: DocsService;
  workspaceService: WorkspaceService;
  file: File;
}

export interface CreateLibraryItemResult {
  docId: string;
  blobId: string;
  duplicateOf?: string;
  /**
   * Id of the `affine:attachment` block created in the new doc. Lets callers
   * open the file directly in the attachment viewer via
   * `workbench.openAttachment(docId, attachmentBlockId)`. Absent on the
   * dedup path (`duplicateOf` set) — resolve it from the existing doc instead.
   */
  attachmentBlockId?: string;
}

/**
 * Upload a file to the workspace blob store and create a library-item
 * doc that renders the file as an `affine:attachment` block. Returns
 * `duplicateOf` (existing docId) when the same blob is already in the
 * library — the workspace blob store is content-addressed, so two
 * uploads of the same bytes always produce the same blobId and we
 * dedup on that key.
 */
export async function createLibraryItem(
  opts: CreateLibraryItemOptions,
  existingLibraryBlobIds: Map<string, string>
): Promise<CreateLibraryItemResult> {
  const { docsService, workspaceService, file } = opts;

  const blobSync = workspaceService.workspace.docCollection.blobSync;
  const blobId = await blobSync.set(file);

  const existingDocId = existingLibraryBlobIds.get(blobId);
  if (existingDocId) {
    return { docId: existingDocId, blobId, duplicateOf: existingDocId };
  }

  const mimeType = file.type || guessMimeFromName(file.name);
  // `onStoreLoad` runs synchronously inside `createDoc` (via `doc.load`), so
  // the captured id is available by the time `createDoc` returns.
  let attachmentBlockId: string | undefined;
  const docProps: DocProps = {
    note: {
      xywh: `[0, 0, ${DEFAULT_PAGE_BLOCK_WIDTH}, ${DEFAULT_PAGE_BLOCK_HEIGHT}]`,
    },
    onStoreLoad: (doc, { noteId, paragraphId }) => {
      // Drop the default placeholder paragraph so the attachment block
      // is the first thing the user sees when opening the library item.
      const placeholder = doc.getModelById(paragraphId);
      if (placeholder) doc.deleteBlock(placeholder);

      attachmentBlockId = doc.addBlock(
        'affine:attachment' as never,
        {
          name: file.name,
          size: file.size,
          type: mimeType,
          sourceId: blobId,
          style: 'horizontalThin',
          embed: false,
        } as Record<string, unknown>,
        noteId
      );
    },
  };

  const docRecord = docsService.createDoc({ primaryMode: 'page', docProps });

  // Custom doc properties only support string values, so numbers go
  // through `String()` on write and are parsed back at read time.
  docRecord.setCustomProperty('kind', LIBRARY_KIND);
  docRecord.setCustomProperty('blobId', blobId);
  docRecord.setCustomProperty('mimeType', mimeType);
  docRecord.setCustomProperty('fileSize', String(file.size));
  docRecord.setCustomProperty('originalName', file.name);
  docRecord.setCustomProperty('uploadedAt', new Date().toISOString());

  docsService.changeDocTitle(docRecord.id, file.name).catch(err => {
    console.warn('[library] failed to set title for upload:', err);
  });

  return { docId: docRecord.id, blobId, attachmentBlockId };
}

function guessMimeFromName(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'pdf':
      return 'application/pdf';
    case 'csv':
      return 'text/csv';
    case 'tsv':
      return 'text/tab-separated-values';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'json':
      return 'application/json';
    case 'txt':
      return 'text/plain';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    default:
      return 'application/octet-stream';
  }
}

export const LIBRARY_FILE_ACCEPT = '.pdf,.csv,.tsv,.xls,.xlsx,.json,.txt,.docx';

const KIND_BY_MIME: Array<{ match: (mime: string) => boolean; label: string }> =
  [
    {
      match: m => m === 'application/pdf',
      label: 'PDF',
    },
    {
      match: m =>
        m === 'text/csv' ||
        m === 'text/tab-separated-values' ||
        m.includes('spreadsheet') ||
        m.includes('excel'),
      label: 'Dataset',
    },
    {
      match: m => m.includes('word'),
      label: 'Document',
    },
    {
      match: m => m === 'application/json' || m.startsWith('text/'),
      label: 'Text',
    },
  ];

export function libraryItemKindLabel(mimeType: string): string {
  const norm = mimeType.toLowerCase();
  for (const entry of KIND_BY_MIME) {
    if (entry.match(norm)) return entry.label;
  }
  return 'File';
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
